/* eslint-disable max-len */
/* eslint-disable react/display-name */
import { VoidCallback } from '@deqo/foundation';
import { tint } from 'polished';
import React, { useState } from 'react';
import { Images } from '../../../@resources/img';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Icon } from '../../../concepts/resources/Icon';
import { Colors } from '../../../ui';
import { DropButton } from '../../../ui/DropButton';
import { useResolve } from '../../Container';
import { useDashboardVersion } from '../../account/AccountHooks';
import { PopoutWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { Copy, Flexer } from '../../dashboard/elements/LayoutElements';
import { editorTemplatePopup } from '../../dashboard/templates/widget/EditorTemplatePopup';
import { PopupManager } from '../../popups/PopupManager';
import { tasksImport } from '../assignment/TaskCommands';
import { jiraImport } from '../atlassian/AtlassianCommands';
import { createGraphicFromPopup } from '../graphics/GraphicCommands';
import { importDataViaLanguageModel } from '../languageModelImport/LanguageModelImportCommands';
import { importDataFromCsvFile } from '../tabularImport/ImportDataFromCsvFilePopup';
import { objectLibraryInsert } from './ObjectLibraryCommands';

export const templateInsert = defineCommand(
    'templateInsert',
    'Template: Insert',
    async (cc, [tag]: [string?]) => {

        const popups = cc.resolve(PopupManager);
        const { actioned, data } = await popups.invoke(editorTemplatePopup(tag));
        if (actioned && data) {
            cc.dispatch(objectLibraryInsert, [data.id, data.label]);
        }
    }
);

type OptionProps = {
    id: string,
    icon: string,
    label: string,
    desc: string,
    onAction: () => void,
    iconStyle?: 'orb' | 'default',
};

const Option = ({ id, icon, label, desc, iconStyle = 'default', onAction }: OptionProps) => {
    const [active, setActive] = useState(false);
    return <DropButton data-action={id} role="button" aria-label={label} active={active} onClick={onAction}>
        <Flexer grow flex="row" gap="1" padding=".5">
            <Flexer flex="row" center shrink color={Colors.primary}>
                {iconStyle === 'default' && <Icon src={icon} size="3" />}
                {iconStyle === 'orb' && <>
                    <Flexer width="3" height="3" flex center background={tint(0.8, Colors.primary)} borderRadius="50%" >
                        <Icon src={icon} size="2.5" />
                    </Flexer>
                </>}
            </Flexer>
            <Flexer flex="column" gap=".25">
                <Flexer flex="row" align="center" justify="space-between">
                    <Copy bold flush size="1.25">{label}</Copy>
                </Flexer>
                <Copy flush size=".875">{desc}</Copy>
            </Flexer>
        </Flexer>
    </DropButton>;
};

export const ObjectLibraryWidget = React.memo<{ widget: PopoutWidget }>(({ widget }) => {

    const dispatch = useResolve(CommandDispatcher);
    const version = useDashboardVersion();

    const doOnClose = (callback: VoidCallback) =>
        () => {
            widget.update({ open: false });
            callback();
        };

    const onTemplate = doOnClose(() => dispatch(templateInsert));
    const onFile = doOnClose(() => dispatch(importDataFromCsvFile));
    const onAI = doOnClose(() => dispatch(importDataViaLanguageModel));
    const onImage = doOnClose(() => dispatch(createGraphicFromPopup));
    const onTasks = doOnClose(() => dispatch(tasksImport));
    const onJira = doOnClose(() => dispatch(jiraImport));

    return <Flexer relative flex width="32" padding="1">
        <Flexer flex="column" gap=".5">
            <Option
                id="editor.toolbar.insert.template"
                label="Template"
                desc="Insert a Metro Retro template into your board."
                icon={Images.LogoBlue}
                onAction={onTemplate}
            />
            {version === 'next' && <Option
                id="editor.toolbar.insert.actions"
                label="Action Items"
                desc="Insert your team's action items."
                icon="deqo://icons/dash/actions"
                iconStyle="orb"
                onAction={onTasks}
            />}
            <Option
                id="editor.toolbar.insert.jira"
                label="Jira Issues"
                desc="Pull issues from Jira directly into your board."
                icon="deqo://icons/i8/color-jira"
                onAction={onJira}
            />
            <Option
                id="editor.toolbar.insert.csv"
                label="CSV File"
                desc="Import CSV data and convert to stickies or cards."
                icon="deqo://icons/i8/color-csv"
                onAction={onFile}
            />
            <Option
                id="editor.toolbar.insert.image"
                label="Image File"
                desc="Import an image and insert into your board."
                icon="deqo://icons/i8/color-image"
                onAction={onImage}
            />
        </Flexer>
    </Flexer>;
});
